import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

export type PartnerGroupOption = {
  id: string;
  name: string;
  isDefault: boolean;
};

type Props = {
  isOpen: boolean;
  title: string;
  description?: string;
  groups: PartnerGroupOption[];
  initialSelectedIds?: string[];
  confirmLabel: string;
  cancelLabel: string;
  loading?: boolean;
  onConfirm: (groupIds: string[]) => void;
  onCancel: () => void;
};

const listClassName =
  'mt-2 max-h-64 space-y-2 overflow-y-auto rounded-md border border-gray-200 p-3 text-left';
const rowClassName =
  'relative flex cursor-pointer items-start rounded-md px-2 py-1.5 hover:bg-gray-50';
const checkboxClassName =
  'h-4 w-4 cursor-pointer rounded border-gray-300 text-indigo-600 focus:ring-indigo-500';

export const PartnerGroupsModal = ({
  isOpen,
  title,
  description,
  groups,
  initialSelectedIds = [],
  confirmLabel,
  cancelLabel,
  loading = false,
  onConfirm,
  onCancel,
}: Props): JSX.Element | null => {
  const { t } = useTranslation('PartnershipScene');
  const [isVisible, setIsVisible] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      setSelectedIds(initialSelectedIds);
    } else {
      setIsVisible(false);
    }
    // Valinta nollataan vain modaalin avautuessa, ei parentin re-rendereillä.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  const toggleGroup = (groupId: string, checked: boolean): void => {
    setSelectedIds((current) =>
      checked ? [...current, groupId] : current.filter((id) => id !== groupId)
    );
  };

  const canConfirm = selectedIds.length > 0 && !loading;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div
        className={`bg-white rounded-lg shadow-lg p-8 max-w-md w-full transform transition-all duration-200 ease-out ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
        }`}
      >
        <h2 className="mb-2 text-lg font-semibold text-gray-900">{title}</h2>
        {description ? <p className="mb-4 text-sm text-gray-color">{description}</p> : null}
        <p className="block text-sm font-medium text-gray-700">{t('selectGroups')}</p>
        <div className={listClassName}>
          {groups.length === 0 ? (
            <p className="text-sm text-gray-color">{t('noGroups')}</p>
          ) : (
            groups.map((group) => (
              <label key={group.id} className={rowClassName}>
                <div className="flex h-5 items-center">
                  <input
                    type="checkbox"
                    className={checkboxClassName}
                    checked={selectedIds.includes(group.id)}
                    onChange={(event) => toggleGroup(group.id, event.target.checked)}
                  />
                </div>
                <span className="ml-3 text-sm text-gray-700">{group.name}</span>
              </label>
            ))
          )}
        </div>
        {selectedIds.length === 0 ? (
          <p className="mt-2 text-sm text-red-600">{t('selectGroupsRequired')}</p>
        ) : null}
        <div className="mt-4 flex justify-center space-x-3">
          <button
            type="button"
            onClick={() => onConfirm(selectedIds)}
            disabled={!canConfirm}
            className="inline-flex justify-center rounded-md border border-transparent bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-secondary focus:outline-none focus:ring-2 focus:ring-secondary focus:ring-offset-2 disabled:bg-gray-300"
          >
            {confirmLabel}
          </button>
          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-secondary focus:ring-offset-2"
          >
            {cancelLabel}
          </button>
        </div>
      </div>
    </div>
  );
};
